import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../../../shared/BaseFragment";
import React, {ReactElement, useState} from "react";
import {BORDER_RADIUS_LARGE, DIVIDER, PD_LG, PD_MD, PD_XLG, PD_XSM, SZ_MD, SZ_SM} from "../../../shared/dimens";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  SvgIcon,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledOrientationComponent, StyledSpan} from "../../../shared/StyledComponents";
import {Action, ActionBase, ActionGroup, ActionType, Orientation, UserProfilePhoto} from "../../../shared/types";
import peppinLogo from "./res/peppin_logotype.svg";
import {BaseApp} from "../../../shared/BaseApp";
import {UserFragment} from "../../../shared/UserFragment";
import {getMemberAuth} from "../../../shared/auth";
import {PathProps} from "../../../index";
import {Close, ExpandLessOutlined, ExpandMoreOutlined, MenuOutlined} from "@mui/icons-material";
import {lightGray} from "../../../shared/colors";


function ActionContentText(props: { action: Action, buttonVariant?: string }): ReactElement {
  return <ButtonBase style={{width: "100%"}}><Typography style={{
    // color: props.buttonVariant === "contained" ? paletteElement.contrastText : paletteElement.main,
    fontWeight: 600,
    textTransform: "none",
  }}>{props.action.text}</Typography>
  </ButtonBase>
}

function getActionContent(action: Action, buttonVariant?: string): ReactElement | string {
  if (action.iconType) {
    const IconType = action.iconType;
    if (action.text) {
      return <StyledBoxRow><IconType/><ActionContentText action={action} buttonVariant={buttonVariant}/></StyledBoxRow>
    } else {
      return <IconType/>
    }
  }
  return <ActionContentText action={action} buttonVariant={buttonVariant}/>;
}

function getActionGroupContent(actionGroup: ActionGroup, buttonVariant?: string): ReactElement | string {
  return <ButtonBase style={{width: "100%"}}><Typography style={{
    // color: props.buttonVariant === "contained" ? paletteElement.contrastText : paletteElement.main,
    fontWeight: 600,
    textTransform: "none",
  }}>{actionGroup.label}</Typography>
  </ButtonBase>;
}

function DrawerActionView(props: { action: Action, indent?: number }) {
  let style = undefined;
  if (props.indent) {
    style = {
      paddingLeft: props.indent * PD_MD,
    };
  }
  return <ListItem disablePadding>
    <ListItemButton onClick={props.action.onClick}>
      <div style={{...style}}>
        {getActionContent(props.action)}
      </div>
    </ListItemButton>
  </ListItem>;
}

function DrawerActionGroupView(props: { actionGroup: ActionGroup, indent?: number }) {
  const [expanded, setExpanded] = useState<boolean>(false);
  return <StyledBoxColumn style={{gap: 0}}>
    <ListItem disablePadding>
      <ListItemButton onClick={(event) => {
        event.stopPropagation();
        setExpanded(!expanded);
      }}>
        {getActionGroupContent(props.actionGroup)}
        {expanded
          ? <ExpandLessOutlined/>
          : <ExpandMoreOutlined/>}
      </ListItemButton>
    </ListItem>
    {expanded
      ? props.actionGroup.actions?.map(actionBase => {
        const indent = (props.indent || 0) + 1;
        switch (actionBase.type) {
          default:
          case ActionType.BUTTON:
            return <DrawerActionView action={actionBase as Action} indent={indent}/>
          case ActionType.GROUP:
            return <DrawerActionGroupView actionGroup={actionBase as ActionGroup} indent={indent}/>;
        }
      })
      : null}
  </StyledBoxColumn>;
}

function PeppinDrawer(props: { actions: ActionBase[] }): ReactElement {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };
  return <>
    {!md ? <>
        <StyledSpan/>
        <IconButton style={{
          width: SZ_SM,
          height: SZ_SM,
          background: lightGray,
        }} onClick={toggleDrawer(true)}>
          <MenuOutlined style={{color: "black"}}/>
        </IconButton>
      </>
      : null}
    <Drawer open={!md && open} anchor="right" onClose={toggleDrawer(false)}>
      <Box sx={{width: 240}} role="presentation" onClick={toggleDrawer(false)}>
        <Box style={{
          display: "flex",
          right: PD_XSM,
          top: PD_XSM,
          alignItems: "center",
          height: SZ_SM,
          position: "absolute",
        }}>
          <IconButton style={{
            width: SZ_SM,
            height: SZ_SM,
            background: lightGray,
          }} onClick={toggleDrawer(false)}>
            <Close style={{color: "black"}}/>
          </IconButton>
        </Box>
        <List style={{paddingTop: SZ_SM + PD_XSM * 2}}>
          {props.actions?.map(actionBase => {
            if (actionBase.type === ActionType.GROUP) {
              const actionGroup = actionBase as ActionGroup;
              return <DrawerActionGroupView actionGroup={actionGroup}/>;
            } else if (!actionBase.type || actionBase.type === ActionType.BUTTON) {
              const action = actionBase as Action;
              return <DrawerActionView action={action}/>;
            }
            return null;
          })}
        </List>
      </Box>
    </Drawer>
  </>;
}

export type HeaderLink = {
  text: string,
  url?: string,
  iconType?: typeof SvgIcon,
  variant?: "contained" | "outlined" | "text",
}

export function HeaderToolbar(props: { path: PathProps, links?: HeaderLink[], actions?: Action[] }): ReactElement {
  const linkActions: Action[] = [];
  const actions: Action[] = [];
  if (props.links) {
    linkActions.push(...props.links.map(link => new Action(link.text, () => props.path.navigate(link.url || "/"), link.iconType)));
    actions.push(...linkActions);
  }
  if (props.actions) {
    actions.push(...props.actions);
  }
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  return <>
    {md
      ? <StyledBoxRow style={{marginLeft: PD_XLG, flexGrow: 1}}>
        {linkActions?.length > 0
          ? <StyledBoxRow style={{gap: PD_XLG}}>{linkActions.map(action => <ButtonBase onClick={action.onClick}>
            <Typography style={{fontWeight: "bold"}}>{action.text}</Typography>
          </ButtonBase>)}
          </StyledBoxRow>
          : null}
        <StyledSpan/>
        {props.actions?.map(action => {
          if (action.display === "icon") {
            return <IconButton onClick={action.onClick}>
              <action.iconType/>
            </IconButton>
          }
          return <Button
            variant={action.variant}
            startIcon={action.iconType ? <action.iconType/> : null}
            endIcon={action.dropdown ? <ExpandMoreOutlined/> : null}
            onClick={action.onClick}>
            {action.text}
          </Button>;
        })}
      </StyledBoxRow>
      : null}
    <PeppinDrawer actions={actions}/>
  </>
}

export function FullscreenContainer(props: { children, title: string, actions: Action[] }) {
  return <StyledBoxColumn style={{height: "100%", gap: 0}}>
    <StyledBoxRow style={{alignItems: "center", height: 72, flexShrink: 0, borderBottom: DIVIDER, padding: PD_LG}}>
      <Typography variant="h6">{props.title}</Typography>
      <StyledSpan/>
      {props.actions.map(action => <Button variant={action.variant} onClick={action.onClick}>
        {action.text}
      </Button>)}
    </StyledBoxRow>
    {props.children}
  </StyledBoxColumn>
}

export function HeaderBar(props: {
  showLoginButton?: boolean,
  path: PathProps,
  links?: HeaderLink[],
  actions?: Action[],
  userActions?: Action[],
  style?: any
}): ReactElement {
  const memberAuth = getMemberAuth();
  const brandHeaderHeight = SZ_MD;
  return <StyledBoxRow
    id="top"
    style={{
      paddingLeft: PD_MD,
      paddingRight: PD_MD,
      width: "100%",
      zIndex: 2,
      flexShrink: 0,
      height: brandHeaderHeight,
      alignItems: "center",
      boxSizing: "border-box",
      gap: PD_XLG,
      ...props.style,
    }}>
    <ButtonBase style={{textDecoration: "none"}} onClick={() => props.path.navigate("/")}>
      <StyledBoxRow style={{alignItems: "center", gap: PD_MD}}>
        <img src={peppinLogo} style={{height: brandHeaderHeight * .5}}/>
        <Typography variant={"h5"}>Showcase</Typography>
      </StyledBoxRow>
    </ButtonBase>
    {/*<TextField size="small" placeholder={"Search"} style={{maxWidth: PAGE_FRAGMENT_WIDTH, flexGrow: 1}}/>*/}
    <HeaderToolbar path={props.path} links={props.links} actions={props.actions}/>
    {Boolean(props.showLoginButton)
      ? (memberAuth?.auth?.currentUser
        ? <StyledBoxRow>
          <Card style={{
            boxSizing: "border-box",
            width: 48,
            height: 48,
            borderRadius: BORDER_RADIUS_LARGE,
            flexShrink: 0,
          }}>
            <ButtonBase
              onClick={() => BaseApp.CONTEXT.showDialog(
                null,
                () => <UserFragment actions={props.userActions} uid={memberAuth?.auth?.currentUser.uid}/>)}
            >
              <img src={UserProfilePhoto(memberAuth?.member?.user)} style={{width: "100%", height: "100%"}}/>
            </ButtonBase>
          </Card>
        </StyledBoxRow>
        : <HeaderButtons actions={[new Action("Login", () => props.path.navigate("/login"))]}/>)
      : null
    }
  </StyledBoxRow>;
}

export function HeaderButtons(props: { actions: Action[] }) {
  return <StyledBoxRow>
    {props.actions.map(action => <Button color="secondary" variant="contained"
                                         onClick={() => action.onClick()}>{action.text}</Button>)}
  </StyledBoxRow>;
}


export function FooterBar(props: { path: PathProps, style?: any }): ReactElement {
  const brandFooterHeight = SZ_MD;
  return <StyledBoxRow
    id="top"
    style={{
      paddingLeft: PD_MD,
      paddingRight: PD_MD,
      width: "100%",
      zIndex: 2,
      flexShrink: 0,
      height: brandFooterHeight,
      alignItems: "center",
      boxSizing: "border-box",
      gap: PD_XLG,
      ...props.style,
    }}>
    <ButtonBase style={{textDecoration: "none"}} onClick={() => props.path.navigate("/")}>
      <img src={peppinLogo} style={{height: brandFooterHeight * .5, opacity: 0.5}}/>
    </ButtonBase>
    <StyledSpan/>
    <StyledBoxRow>
      <a href="/terms"><Typography variant="body2">Terms</Typography></a>
      <a href="/privacy"><Typography variant="body2">Privacy</Typography></a>
      <a href="https://superfyne.com"><Typography
        variant="body2">{`© ${new Date().getFullYear()} Superfyne`}</Typography></a>
    </StyledBoxRow>
  </StyledBoxRow>;
}

export type ToolOption = {
  id: string,
  text?: string,
  image: string,
}

export type ToolOptions = {
  title: string,
  options: ToolOption[],
}

export type Tool = {
  id: string,
  text: string,
  image: string | "preview",
  toolOptions: ToolOptions,
  defaultToolOptionId: string,
}

export interface PPNThreePaneFragmentListener {
  onToolSelected(toolId: string): void;

  onToolOptionsSelected(toolOptionIds: Map<string, string>): void;
}

export type PeppinThreePaneFragmentProps = BaseFragmentProps & {
  tools: Tool[],
  defaultToolOptions?: Map<string, string>,
  toolsPaneSize?: "sm" | "md" | "lg",
  listener: PPNThreePaneFragmentListener,
  children: any,
}

type PeppinThreePaneFragmentState = BaseFragmentState & {
  selectedToolId?: string,
  selectedToolOptionIds?: Map<string, string>,
}

const DIVIDER_SELECT = "4px solid black";
const DIVIDER_NOSELECT = "4px solid transparent";

export class PeppinThreePaneFragment extends BaseFragment<PeppinThreePaneFragmentProps, PeppinThreePaneFragmentState> {

  setSelectedToolOptionIds(selectedToolOptionIds: Map<string, string>) {
    this.setState({
      selectedToolOptionIds
    });
  }

  protected containerContentDidRender() {
    super.containerContentDidRender();
    let selectedToolOptionIds;
    if (this.props.defaultToolOptions) {
      selectedToolOptionIds = this.props.defaultToolOptions;
    } else {
      selectedToolOptionIds = new Map<string, string>();
      this.props.tools.forEach(tool => selectedToolOptionIds.set(tool.id, tool.defaultToolOptionId));
    }
    this.setState({
      selectedToolId: this.props.tools[0].id,
      selectedToolOptionIds: selectedToolOptionIds,
    });
  }

  componentDidUpdate(prevProps: Readonly<PeppinThreePaneFragmentProps>, prevState: Readonly<PeppinThreePaneFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.selectedToolId !== this.state.selectedToolId) {
      this.props.listener.onToolSelected(this.state.selectedToolId);
    }
    if (prevState.selectedToolOptionIds !== this.state.selectedToolOptionIds) {
      this.props.listener.onToolOptionsSelected(this.state.selectedToolOptionIds)
    }
  }

  protected renderContainerContent(): React.ReactElement | null {
    if (!this.state.selectedToolId) {
      return null;
    }
    const selectedTool = this.props.tools.find(tool => tool.id === this.state.selectedToolId);
    const selectedToolOption = selectedTool.toolOptions.options.find(option => option.id === this.state.selectedToolOptionIds.get(selectedTool.id));
    return <StyledOrientationComponent
      breakpoint={"md"}
      render={orientation => <div
        style={{
          display: "flex",
          flexDirection: orientation === Orientation.LANDSCAPE ? "row" : "column",
          width: "100vw",
          height: "100%",
        }}>
        {orientation === Orientation.LANDSCAPE
          ? this.renderLandscape(selectedTool, selectedToolOption)
          : this.renderPortrait(selectedTool, selectedToolOption)}
      </div>}/>
  }

  private renderLandscape(selectedTool: Tool, selectedToolOption: ToolOption): ReactElement {
    return <>
      <div
        className="hidescroll"
        style={{
          display: "flex", flexDirection: "column", width: 72, flexShrink: 0, borderRight: DIVIDER, overflowY: "scroll",
        }}>
        {this.renderTools(selectedTool, Orientation.LANDSCAPE)}
      </div>
      <div
        className="hidescroll"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: 360,
          flexShrink: 0,
          borderRight: DIVIDER,
          overflow: "scroll",
        }}>
        <Grid container>
          {this.renderToolOptions(selectedTool, selectedToolOption, Orientation.LANDSCAPE)}
        </Grid>
      </div>
      <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
        {this.props.children}
      </div>
    </>;
  }

  private renderPortrait(selectedTool: Tool, selectedToolOption: ToolOption): ReactElement {
    return <>
      <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
        {this.props.children}
      </div>
      <div
        className="hidescroll"
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: 360,
          flexShrink: 0,
          borderTop: DIVIDER,
          overflow: "scroll",
        }}>
        <Grid container>
          {this.renderToolOptions(selectedTool, selectedToolOption, Orientation.PORTRAIT)}
        </Grid>
      </div>
      <div
        className="hidescroll"
        style={{
          display: "flex", flexDirection: "row", height: 72, flexShrink: 0, borderTop: DIVIDER, overflowX: "scroll",
        }}>
        {this.renderTools(selectedTool, Orientation.PORTRAIT)}
      </div>
    </>;
  }

  private renderToolOptions(selectedTool: Tool, selectedToolOption: ToolOption, orientation: Orientation) {
    return <>
      {selectedTool.toolOptions.options.map(option => <Grid xs={4}>
        <ButtonBase
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: selectedToolOption.id === option.id ? DIVIDER_SELECT : DIVIDER_NOSELECT
          }}
          onClick={() => {
            const map = new Map<string, string>(this.state.selectedToolOptionIds.entries());
            map.set(selectedTool.id, option.id);
            this.setState({selectedToolOptionIds: map});
          }}>
          <Tooltip title={option.text}>
            <img src={option.image}
                 style={{
                   width: "100%",
                   aspectRatio: 1,
                   padding: 12,
                   boxSizing: "border-box",
                   objectFit: "contain"
                 }}/>
          </Tooltip>
        </ButtonBase>
      </Grid>)}
    </>;
  }

  private renderTools(selectedTool: Tool, orientation: Orientation) {
    return <>
      {this.props.tools.map(tool => <ButtonBase
        style={{boxSizing: "border-box", border: selectedTool.id === tool.id ? DIVIDER_SELECT : DIVIDER_NOSELECT}}
        onClick={() => this.setState({selectedToolId: tool.id})}>
        <Tooltip title={tool.text} placement={orientation === Orientation.LANDSCAPE ? "right" : "top"}>
          <img
            src={tool.image === "preview" ? tool.toolOptions.options.find(option => option.id === this.state.selectedToolOptionIds.get(tool.id)).image : tool.image}
            style={{width: 72, height: 72, padding: 12, boxSizing: "border-box", objectFit: "contain"}}/>
        </Tooltip>
      </ButtonBase>)}
    </>;
  }
}